import { Select } from '@glu/form-components';
import locale from '@glu/locale';
import React from 'react';
import { withTheme } from '@glu/theming';
import PropTypes from 'prop-types';
import {
  getDefaultDayForInterval, DAY, MON, SAT, SUN, TUE, THU, FRI, WED
} from '../utils/recurrence';
import TimePicker from './TimePicker';
import useStyles from './DaySelect.styles';
import { getDayOptions, getOrdinalDayOptions, getOrdinalOptions } from './Monthly.options';

const DaySelect = ({
  dayInMonth, allowWeekend, canSetTime, hideLastDayInDays, timePicker24Hour, updateType,
  showingMultipleDays, disabled, index,
  minTime, maxTime, hideMinutes, onChange, ...rest
}) => {
  const classes = useStyles({
    timePicker24Hour, showingMultipleDays, canSetTime, ...rest
  });

  const ordinalOptions = getOrdinalOptions(locale, hideLastDayInDays);
  const ordinalDayOptions = getOrdinalDayOptions(locale);
  const dayOptions = getDayOptions(locale, allowWeekend);

  return (
    <>
      <div className={classes.datePicker}>
        <Select
          htmlId={`${dayInMonth.id}-ordinalSelect`}
          name={`${dayInMonth.id}-ordinalSelect`}
          data-qa="monthly-ordinal-select"
          className={classes.ordinalSelect}
          labelText={locale.get('schedulerReact.onThe')}
          options={dayInMonth.intervalType === DAY ? ordinalOptions : ordinalDayOptions}
          value={dayInMonth.day.toString()}
          screenReaderLabel={index !== 0}
          disabled={disabled}
          disableInputAPI
          onChange={(e) => onChange({
            type: updateType,
            response: {
              ...dayInMonth,
              day: e.target.value
            },
            index
          })}
        />
        <Select
          htmlId={`${dayInMonth.id}-intervalTypeSelect`}
          name={`${dayInMonth.id}-intervalTypeSelect`}
          data-qa="interval-type-select"
          className={classes.intervalTypeSelect}
          labelText={locale.get('schedulerReact.onThe')}
          screenReaderLabel
          options={dayOptions}
          value={dayInMonth.intervalType}
          disabled={disabled}
          disableInputAPI
          onChange={(e) => onChange({
            type: updateType,
            response: {
              ...dayInMonth,
              day: getDefaultDayForInterval(
                dayInMonth.intervalType, e.target.value, dayInMonth.day
              ),
              intervalType: e.target.value
            },
            index
          })}
        />
      </div>
      {canSetTime && (
        <TimePicker
          htmlId={`${dayInMonth.id}-timePicker`}
          onChange={(time) => onChange({
            type: updateType,
            response: {
              ...dayInMonth,
              atTime: time.value
            },
            index
          })}
          minTime={minTime}
          maxTime={maxTime}
          value={dayInMonth.atTime}
          className={classes.timePicker}
          screenReaderLabel={index !== 0}
          hideMinutes={hideMinutes}
        />
      )}
    </>
  );
};

DaySelect.propTypes = {
  dayInMonth: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    atTime: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    day: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    intervalType: PropTypes.oneOf([DAY, MON, TUE, WED, THU, FRI, SAT, SUN])
  }),

  timePicker24Hour: PropTypes.bool,
  showingMultipleDays: PropTypes.bool,
  disabled: PropTypes.bool,
  index: PropTypes.number,
  updateType: PropTypes.string.isRequired,

  /** Option to set time on day */
  canSetTime: PropTypes.bool,

  /** Hides minute controls, only displaying hours to control * */
  hideMinutes: PropTypes.bool,

  /** Controls whether to display weekends in list of days */
  allowWeekend: PropTypes.bool,

  /** Hide 'last' day in days dropdown */
  hideLastDayInDays: PropTypes.bool,

  /** Minimum time value of input */
  minTime: PropTypes.string,

  /** Maximum time value of input */
  maxTime: PropTypes.string,

  /** Callback to set recurrence value */
  onChange: PropTypes.func.isRequired
};

DaySelect.defaultProps = {
  dayInMonth: {
    day: 1,
    intervalType: DAY
  },
  allowWeekend: false,
  canSetTime: true,
  hideLastDayInDays: false,
  minTime: undefined,
  maxTime: undefined,
  hideMinutes: false,
  timePicker24Hour: false,
  disabled: false,
  showingMultipleDays: false,
  index: 0
};

export default withTheme(DaySelect);
